import { initComponent } from './core/init-component';

initComponent('.js-category-select', (section) => {
  const items = Array.from(section.querySelectorAll<HTMLAnchorElement>('.js-cat-item'));
  const imagesLeft = section.querySelectorAll<HTMLImageElement>('.js-cat-image-left');
  const imagesRight = section.querySelectorAll<HTMLImageElement>('.js-cat-image-right');
  const imagesMobile = section.querySelectorAll<HTMLImageElement>('.js-cat-image-mobile');

  const HOVER_OFFSET = 100;
  const MOBILE_OFFSET = 24;
  const MOBILE_OFFSET_LONG = 8;
  const LONG_TITLE_THRESHOLD = 8;

  const isDesktop = () => window.innerWidth >= 1024;

  // Long titles overflow the mobile viewport; use a smaller offset (keep in sync with category-select.liquid).
  const getMobileOffset = (item: HTMLAnchorElement): number => {
    const length = item.textContent?.trim().length ?? 0;
    return length > LONG_TITLE_THRESHOLD ? MOBILE_OFFSET_LONG : MOBILE_OFFSET;
  };

  const getIndex = (value: string | undefined): number => {
    const n = Number(value);
    return Number.isNaN(n) ? 0 : n;
  };

  const getInner = (item: HTMLAnchorElement) => {
    return item.querySelector<HTMLElement>('.js-cat-item-inner');
  };

  const baseMap = new Map<HTMLAnchorElement, number>();

  items.forEach(item => {
    const inner = getInner(item);

    if (!inner) return;

    const computed = getComputedStyle(inner).transform;

    if (computed === 'none') {
      baseMap.set(item, 0);
      return;
    }

    const matrix = new DOMMatrix(computed);
    baseMap.set(item, matrix.m41);
  });

  const resetImages = () => {
    imagesLeft.forEach(img => (img.style.opacity = '0'));
    imagesRight.forEach(img => (img.style.opacity = '0'));
    imagesMobile.forEach(img => (img.style.opacity = '0'));
  };

  const setItemOffset = (item: HTMLAnchorElement, offset: number) => {
    const inner = getInner(item);
    if (!inner) return;

    inner.style.transform = `translateX(${offset}px)`;
  };

  items.forEach(item => {
    const index = getIndex(item.dataset.index);
    const base = baseMap.get(item) ?? 0;

    item.addEventListener('mouseenter', () => {
      if (!isDesktop()) return;

      resetImages();

      imagesLeft[index]?.style.setProperty('opacity', '1');
      imagesRight[index]?.style.setProperty('opacity', '1');

      setItemOffset(item, base + HOVER_OFFSET);
    });

    item.addEventListener('mouseleave', () => {
      if (!isDesktop()) return;      

      setItemOffset(item, base);
    });
  });

  // Mobile scroll behavior

  let lastScrollY = window.scrollY;
  let scrollingDown = true;
  let mobileFrame: number | null = null;

  const activated = new Set<HTMLAnchorElement>();

  const moveMobileItem = (item: HTMLAnchorElement) => {
    const index = items.indexOf(item);
    const base = baseMap.get(item) ?? 0;
    const direction = index % 3 === 2 ? 1 : -1;

    resetImages();
    imagesMobile[index]?.style.setProperty('opacity', '1');

    setItemOffset(item, base + getMobileOffset(item) * direction);

    activated.add(item);
  };

  const resetMobileItem = (item: HTMLAnchorElement) => {
    const index = items.indexOf(item);
    const base = baseMap.get(item) ?? 0;

    resetImages();
    imagesMobile[index]?.style.setProperty('opacity', '1');

    setItemOffset(item, base);

    activated.delete(item);
  };

  const isActuallyVisible = (item: HTMLAnchorElement) => {
    const rect = item.getBoundingClientRect();

    if (rect.bottom <= 0 || rect.top >= window.innerHeight) return false;

    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const topElement = document.elementFromPoint(x, y);

    return topElement === item || !!topElement?.closest('.js-cat-item')?.isSameNode(item);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      if (isDesktop() || !scrollingDown) return;

      const visible = entries
        .filter(entry => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

      if (!visible) return;

      const item = visible.target as HTMLAnchorElement;

      if (activated.has(item)) return;

      moveMobileItem(item);
    },
    {
      root: null,
      threshold: [0.3, 0.6],
    }
  );

  items.forEach(item => observer.observe(item));
  
  let mobileResetLocked = false;
  window.addEventListener('scroll', () => {
    const current = window.scrollY;
    scrollingDown = current > lastScrollY;
    lastScrollY = current;

    if (isDesktop() || scrollingDown || mobileFrame !== null) return;

    mobileFrame = requestAnimationFrame(() => {
      mobileFrame = null;

      const visibleActivated = Array.from(activated)
        .filter(item => isActuallyVisible(item))
        .sort((a, b) => {
          const rectA = a.getBoundingClientRect();
          const rectB = b.getBoundingClientRect();

          return rectB.top - rectA.top;
        });

      const itemToReset = visibleActivated[0];

      if (itemToReset && !mobileResetLocked) {
        mobileResetLocked = true;

        resetMobileItem(itemToReset);

        setTimeout(() => {
          mobileResetLocked = false;
        }, 120);
      }
    });
  }, { passive: true });

  // Hover hack on scroll (desktop only)

  let activeDesktopItem: HTMLAnchorElement | null = null;
  let pointerX = window.innerWidth / 2;
  let pointerY = window.innerHeight / 2;
  let scrollFrame: number | null = null;

  const activateDesktopItem = (item: HTMLAnchorElement | null) => {
    if (!isDesktop()) return;
    if (activeDesktopItem === item) return;

    if (activeDesktopItem) {
      const base = baseMap.get(activeDesktopItem) ?? 0;
      setItemOffset(activeDesktopItem, base);
    }

    resetImages();

    activeDesktopItem = item;

    if (!item) return;

    const index = getIndex(item.dataset.index);
    const base = baseMap.get(item) ?? 0;

    imagesLeft[index]?.style.setProperty('opacity', '1');
    imagesRight[index]?.style.setProperty('opacity', '1');

    setItemOffset(item, base + HOVER_OFFSET);
  };

  window.addEventListener('pointermove', e => {
    pointerX = e.clientX;
    pointerY = e.clientY;
  }, { passive: true });

  window.addEventListener('scroll', () => {
    if (scrollFrame !== null) return;

    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = null;

      if (!isDesktop()) return;

      const el = document.elementFromPoint(pointerX, pointerY);
      const item = el?.closest<HTMLAnchorElement>('.js-cat-item') ?? null;

      if (item && section.contains(item)) {
        activateDesktopItem(item);
      }
    });
  }, { passive: true });
});